import { useApolloClient, useQuery } from "@apollo/client"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-hot-toast"
import invariant from "tiny-invariant"
import { getFragmentData } from "~/__generated__"
import { BulkUploadResultFragment } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/useSafeMutation"
import { ROSTER_DATABASE_QUERY_DOCUMENT } from "~/screens/rosters/api"
import { trackEvent } from "~/util/tracking"
import { Alert, AlertDescription } from "~/shadcn/ui/alert"
import { Button } from "~/shadcn/ui/button"
import { Hr } from "~/ui/Hr"
import { H2 } from "~/ui/typography"
import {
  BULK_UPLOAD_INFLUENCERS_MUTATION,
  BULK_UPLOAD_RESULT_FRAGMENT,
  BULK_UPLOAD_STATUS_QUERY,
} from "./api"
import { BulkUpload } from "./components/BulkUpload"
import { BulkUploadResults } from "./components/BulkUploadResults"
import { HelpText } from "./components/HelpText"

const POLL_INTERVAL = 500
const STUCK_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

type ImportState =
  | "idle"
  | "submitting"
  | "polling"
  | "completed"
  | "failed"
  | "stuck"

export const BulkUploadScreen = ({ onClose }: { onClose: () => void }) => {
  const client = useApolloClient()
  const [runBulkUpload, bulkUploadResult] = useSafeMutation(
    BULK_UPLOAD_INFLUENCERS_MUTATION
  )
  // The preview lives in state rather than being read off bulkUploadResult.data:
  // the real (non-dry-run) mutation returns result: null, so reading the latest
  // mutation data would blank the preview and permanently disable the submit
  // button the moment the user clicks it.
  const [previewResult, setPreviewResult] =
    useState<BulkUploadResultFragment | null>(null)
  // Likewise the terminal errors: reaching a terminal importState makes the
  // status query skip, which drops statusData before the alert can render them.
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [csvSignedId, setCsvSignedId] = useState<string | null>(null)
  const [importState, setImportState] = useState<ImportState>("idle")
  const [bulkUploadId, setBulkUploadId] = useState<string | null>(null)
  const pollingStartedAt = useRef<number | null>(null)
  const handledTerminalState = useRef(false)

  const { data: statusData, stopPolling } = useQuery(BULK_UPLOAD_STATUS_QUERY, {
    variables: { id: bulkUploadId || "" },
    skip: !bulkUploadId || !["polling", "stuck"].includes(importState),
    pollInterval: POLL_INTERVAL,
  })

  const uploadStatus = statusData?.influencerBulkUpload

  useEffect(() => {
    if (!["polling", "stuck"].includes(importState) || !uploadStatus) return
    if (handledTerminalState.current) return

    if (
      uploadStatus.status === "completed" ||
      uploadStatus.status === "failed"
    ) {
      handledTerminalState.current = true
      stopPolling()

      trackEvent("bulk_upload_completed", {
        rows_total: uploadStatus.totalInfluencerCount ?? 0,
        success_count:
          (uploadStatus.newInfluencerCount ?? 0) +
          (uploadStatus.existingInfluencerCount ?? 0),
        error_count: uploadStatus.errors.length,
      })

      if (
        uploadStatus.status === "completed" &&
        uploadStatus.errors.length === 0
      ) {
        client.refetchQueries({ include: [ROSTER_DATABASE_QUERY_DOCUMENT] })
        toast.success("Influencers added / updated successfully")
        onClose()
      } else if (
        uploadStatus.status === "completed" &&
        uploadStatus.errors.length > 0
      ) {
        setImportErrors(uploadStatus.errors)
        setImportState("completed")
      } else {
        setImportState("failed")
      }
      return
    }

    // Stuck detection
    if (pollingStartedAt.current) {
      const elapsed = Date.now() - pollingStartedAt.current
      if (elapsed > STUCK_TIMEOUT_MS) {
        setImportState("stuck")
      }
    }
  }, [uploadStatus, importState, stopPolling, onClose, client])

  const onSubmit = async () => {
    invariant(csvSignedId, "uploaded csv is required")
    setImportErrors([])
    setImportState("submitting")

    const { data } = await runBulkUpload({
      variables: {
        input: {
          csvFile: csvSignedId,
          dryRun: false,
        },
      },
    })

    const uploadId = data?.influencerBulkUpload?.bulkUpload?.id
    if (uploadId) {
      setBulkUploadId(uploadId)
      pollingStartedAt.current = Date.now()
      handledTerminalState.current = false
      setImportState("polling")
    } else {
      setImportState("failed")
    }
  }

  const isImporting =
    importState === "submitting" ||
    importState === "polling" ||
    importState === "stuck"

  return (
    <div className="flex-1 p-8 pb-4 flex flex-col">
      <div>
        <H2 className="mb-2">Upload new roster data</H2>
        <p className="text-gray50 mb-4">
          Add new influencers to your roster or update your existing roster
        </p>
        <Hr />
      </div>
      <div className="flex-grow">
        <BulkUpload
          onUpload={async (signedId) => {
            setCsvSignedId(signedId)
            setImportState("idle")
            setBulkUploadId(null)
            setImportErrors([])
            setPreviewResult(null)
            const { data } = await runBulkUpload({
              variables: {
                input: {
                  csvFile: signedId,
                  dryRun: true,
                },
              },
            })
            setPreviewResult(
              getFragmentData(
                BULK_UPLOAD_RESULT_FRAGMENT,
                data?.influencerBulkUpload?.result
              ) ?? null
            )
          }}
          extraContent={
            <>
              <BulkUploadResults
                result={previewResult ?? undefined}
                errorDescription="Found errors in csv file. Please try to fix the file and re-upload the updated file."
              />
              {importState === "completed" && importErrors.length > 0 && (
                <Alert variant="error" className="mt-4 w-auto">
                  <AlertDescription>
                    <div>
                      No influencers were imported. Please fix the errors below
                      and re-upload.
                    </div>
                    <ul className="list-disc mt-2 ml-4">
                      {importErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              {importState === "failed" && (
                <Alert variant="error" className="mt-4 w-auto">
                  <AlertDescription>
                    Something went wrong processing your upload. Please try
                    again or contact support.
                  </AlertDescription>
                </Alert>
              )}
              {importState === "stuck" && (
                <Alert variant="warning" className="mt-4 w-auto">
                  <AlertDescription>
                    Your upload appears to be taking longer than expected.
                    Please check your roster and try again if needed.
                  </AlertDescription>
                </Alert>
              )}
              <HelpText />
            </>
          }
          processing={bulkUploadResult.loading || isImporting}
        />
      </div>
      <div className="flex justify-between bg-white py-4">
        <Button
          type="button"
          variant="linkSecondary"
          size="none"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!previewResult?.success || isImporting}
        >
          {isImporting ? "Importing..." : "Add / Update Influencers"}
        </Button>
      </div>
    </div>
  )
}
